import type { Node, TextNodeData, ImageNodeData, IframeNodeData } from '../../../types';
import { extractNodeId } from '../../../utils/nodeTransformers';
import { logger } from '../../../utils/logger';
import { getRandomEmoji } from 'random-emoji-picker';

interface UseNodeOperationsProps {
  currentSlideId: number | null;
  nodes: Node[];
  setNodes: (nodes: Node[] | ((prev: Node[]) => Node[])) => void;
  setSlideNodesCache: (cache: { [slideId: number]: Node[] } | ((prev: { [slideId: number]: Node[] }) => { [slideId: number]: Node[] })) => void;
  apiUrl: (path: string) => string;
  canEdit: boolean;
  setRecentImages: (images: string[]) => void;
  setRecentUrls: (urls: string[] | ((prev: string[]) => string[])) => void;
  setShowUrlPanel: (show: boolean) => void;
  updateQueueHook: {
    flushUpdateQueue: () => Promise<void>;
    queueNodeUpdate: (id: string, updates: Partial<Node>, debounceMs: number) => void;
    queueNodeDataUpdate: (id: string, dataUpdates: Partial<TextNodeData> | Partial<ImageNodeData> | Partial<IframeNodeData>, debounceMs: number) => void;
  };
}

/**
 * Hook for node CRUD operations
 *
 * Handles:
 * - Creating nodes (text, image, iframe)
 * - Updating node position, size, data
 * - Deleting nodes
 * - Duplicating nodes
 * - Z-index management
 * - Frame style toggling
 */
export function useNodeOperations({
  currentSlideId,
  nodes,
  setNodes,
  setSlideNodesCache,
  apiUrl,
  canEdit,
  setRecentImages,
  setRecentUrls,
  setShowUrlPanel,
  updateQueueHook,
}: UseNodeOperationsProps) {

  const loadRecentImages = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    try {
      const response = await fetch(apiUrl('/slides/0/nodes/recent_images'), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to load recent images');

      const data = await response.json();
      setRecentImages(data.images.map((img: { url: string }) => img.url));
      logger.debug('api', 'Loaded recent images', { count: data.images.length });
    } catch (error) {
      logger.error('api', 'Failed to load recent images', error);
    }
  };

  const updateNode = (id: string, updates: Partial<Node>) => {
    setNodes(prevNodes => {
      const updated = prevNodes.map(node =>
        node.id === id ? { ...node, ...updates } : node
      );
      if (currentSlideId) {
        setSlideNodesCache(prev => ({ ...prev, [currentSlideId]: updated }));
      }
      return updated;
    });

    if (canEdit) {
      updateQueueHook.queueNodeUpdate(id, updates, 300);
    }
  };

  const updateNodeData = (id: string, dataUpdates: Partial<TextNodeData> | Partial<ImageNodeData> | Partial<IframeNodeData>) => {
    setNodes(prevNodes => {
      const updated = prevNodes.map(node =>
        node.id === id
          ? { ...node, data: { ...node.data, ...dataUpdates } as TextNodeData | ImageNodeData | IframeNodeData }
          : node
      );
      if (currentSlideId) {
        setSlideNodesCache(prev => ({ ...prev, [currentSlideId]: updated }));
      }
      return updated;
    });

    if (canEdit) {
      updateQueueHook.queueNodeDataUpdate(id, dataUpdates, 500);
    }
  };

  const deleteNode = async (id: string) => {
    await updateQueueHook.flushUpdateQueue();

    const node = nodes.find(n => n.id === id);
    const updatedNodes = nodes.filter(node => node.id !== id);
    setNodes(updatedNodes);
    if (currentSlideId) {
      setSlideNodesCache(prev => ({ ...prev, [currentSlideId]: updatedNodes }));
    }

    const nodeId = extractNodeId(id);

    try {
      const response = await fetch(apiUrl(`/slides/${currentSlideId}/nodes/${nodeId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok && response.status !== 404) {
        logger.error('api', `Failed to delete node ${nodeId}: ${response.status}`, { status: response.status });
      } else if (response.ok) {
        logger.info('api', `Deleted node ${nodeId}`);
        if (node && (node.type === 'image' || node.type === 'video')) {
          loadRecentImages();
        }
      }
    } catch (error) {
      logger.error('api', 'Failed to delete node', error);
    }
  };

  const duplicateNode = async (id: string) => {
    if (!currentSlideId) return;

    await updateQueueHook.flushUpdateQueue();

    const node = nodes.find(n => n.id === id);
    if (!node) return;

    const maxZ = nodes.length > 0 ? Math.max(...nodes.map(n => n.zIndex)) : 0;
    const newZ = maxZ + 1;

    try {
      const nodeData: any = {
        node_type: node.type,
        position: { x: node.position.x + 5, y: node.position.y + 5 },
        size: node.size,
        z_index: newZ,
        frame_style: node.data.frameStyle,
      };

      if (node.type === 'text') {
        nodeData.content = node.data.content;
        nodeData.style = 'style' in node.data ? node.data.style : {};
        nodeData.sender_name = 'senderName' in node.data ? node.data.senderName : undefined;
        nodeData.avatar_url = 'avatarUrl' in node.data ? node.data.avatarUrl : undefined;
        nodeData.avatar_emoji = 'avatarEmoji' in node.data ? node.data.avatarEmoji : undefined;
        nodeData.background_color = 'backgroundColor' in node.data ? node.data.backgroundColor : undefined;
        nodeData.work_in_progress = 'workInProgress' in node.data ? node.data.workInProgress : undefined;
        nodeData.border_radius = 'borderRadius' in node.data ? node.data.borderRadius : undefined;
        nodeData.wip_banner_offset = 'wipBannerOffset' in node.data ? node.data.wipBannerOffset : undefined;
      } else if (node.type === 'iframe') {
        nodeData.source_url = node.data.content;
        nodeData.title = 'title' in node.data ? node.data.title : '';
        nodeData.work_in_progress = 'workInProgress' in node.data ? node.data.workInProgress : undefined;
        nodeData.border_radius = 'borderRadius' in node.data ? node.data.borderRadius : undefined;
        nodeData.wip_banner_offset = 'wipBannerOffset' in node.data ? node.data.wipBannerOffset : undefined;
        nodeData.iframe_zoom = 'iframeZoom' in node.data ? node.data.iframeZoom : undefined;
      } else {
        nodeData.source_url = node.data.content;
        nodeData.title = 'title' in node.data ? node.data.title : '';
        nodeData.work_in_progress = 'workInProgress' in node.data ? node.data.workInProgress : undefined;
        nodeData.border_radius = 'borderRadius' in node.data ? node.data.borderRadius : undefined;
        nodeData.wip_banner_offset = 'wipBannerOffset' in node.data ? node.data.wipBannerOffset : undefined;
      }

      const response = await fetch(apiUrl(`/slides/${currentSlideId}/nodes`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ node: nodeData }),
      });

      if (!response.ok) throw new Error('Failed to duplicate node');

      const data = await response.json();
      logger.info('api', 'Node duplicated', { nodeId: data.node.id });

      const newNode: Node = {
        id: `node-${data.node.id}`,
        type: node.type,
        position: data.node.position || nodeData.position,
        size: data.node.size || node.size,
        zIndex: data.node.z_index || newZ,
        data: node.data
      };

      setNodes(prevNodes => {
        const updated = [...prevNodes, newNode];
        if (currentSlideId) {
          setSlideNodesCache(prev => ({ ...prev, [currentSlideId]: updated }));
        }
        return updated;
      });
    } catch (error) {
      logger.error('api', 'Failed to duplicate node', error);
    }
  };

  const bringToFront = (nodeId: string) => {
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;

    const maxZ = Math.max(...nodes.map(n => n.zIndex));
    const newZ = maxZ + 1;

    setNodes(prevNodes =>
      prevNodes.map(n =>
        n.id === nodeId ? { ...n, zIndex: newZ } : n
      )
    );
    updateQueueHook.queueNodeUpdate(nodeId, { zIndex: newZ }, 500);
  };

  const toggleFrameStyle = async (id: string) => {
    await updateQueueHook.flushUpdateQueue();

    const node = nodes.find(n => n.id === id);
    if (!node) return;

    if (node.type === 'text') {
      const textData = node.data as TextNodeData;
      const styles: Array<TextNodeData['frameStyle']> = ['browser', 'notepad', 'message', 'solid', 'none'];
      const currentIndex = styles.indexOf(textData.frameStyle);
      const nextIndex = (currentIndex + 1) % styles.length;
      updateNodeData(id, { frameStyle: styles[nextIndex] });
    } else if (node.type === 'image' || node.type === 'video' || node.type === 'iframe') {
      const mediaData = node.data as ImageNodeData | IframeNodeData;
      const styles: Array<'browser' | 'none'> = ['browser', 'none'];
      const currentIndex = styles.indexOf(mediaData.frameStyle as 'browser' | 'none');
      const nextIndex = (currentIndex + 1) % styles.length;
      updateNodeData(id, { frameStyle: styles[nextIndex] });
    }
  };

  const addTextNode = async () => {
    if (!currentSlideId) return;

    const avatarEmoji = getRandomEmoji();
    const maxZ = nodes.length > 0 ? Math.max(...nodes.map(n => n.zIndex)) : 0;
    const newZ = maxZ + 1;

    const tiptapContent = JSON.stringify({
      type: 'doc',
      content: [
        {
          type: 'paragraph',
          attrs: { textAlign: 'center' },
          content: [
            {
              type: 'text',
              text: 'New text node',
              marks: [
                { type: 'italic' },
                {
                  type: 'textStyle',
                  attrs: {
                    fontSize: '48px',
                    fontFamily: 'Test National',
                  },
                },
              ],
            },
          ],
        },
      ],
    });

    try {
      const response = await fetch(apiUrl(`/slides/${currentSlideId}/nodes`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          node: {
            node_type: 'text',
            content: tiptapContent,
            frame_style: 'browser',
            position: { x: 50, y: 50 },
            size: { width: 'auto', height: 'auto' },
            z_index: newZ,
            avatar_emoji: avatarEmoji,
            background_color: 'white',
          }
        }),
      });

      if (!response.ok) throw new Error('Failed to create node');

      const data = await response.json();
      const newNode: Node = {
        id: `node-${data.node.id}`,
        type: 'text',
        position: data.node.position || { x: 50, y: 50 },
        size: data.node.size || { width: 'auto', height: 'auto' },
        zIndex: data.node.z_index || newZ,
        data: {
          content: data.node.content,
          frameStyle: data.node.frame_style,
          style: data.node.style || {},
          senderName: data.node.sender_name,
          avatarUrl: data.node.avatar_url,
          avatarEmoji: data.node.avatar_emoji || avatarEmoji,
          backgroundColor: data.node.background_color || 'white'
        }
      };
      setNodes(prevNodes => {
        const updated = [...prevNodes, newNode];
        if (currentSlideId) {
          setSlideNodesCache(prev => ({ ...prev, [currentSlideId]: updated }));
        }
        return updated;
      });
    } catch (error) {
      console.error('Failed to add text node:', error);
    }
  };

  const addImageNode = async (file: File) => {
    if (!currentSlideId) return;

    const isVideo = file.type.startsWith('video/') || /\.(mp4|webm|ogg|mov)$/i.test(file.name);
    const maxZ = nodes.length > 0 ? Math.max(...nodes.map(n => n.zIndex)) : 0;
    const newZ = maxZ + 1;

    logger.info('upload', `Starting ${isVideo ? 'video' : 'image'} upload`, { fileName: file.name, fileType: file.type });

    try {
      let width: number;
      let height: number;

      if (isVideo) {
        width = 80;
        height = 45;
        logger.debug('upload', 'Using default video dimensions', { width, height });
      } else {
        const img = new Image();
        const imageDataUrl = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.readAsDataURL(file);
        });

        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = reject;
          img.src = imageDataUrl;
        });

        const maxWidth = 60;
        const maxHeight = 60;
        const frameOverheadPercent = -3;
        const aspectRatio = img.width / img.height;

        let imageWidth = maxWidth;
        let imageHeight = maxWidth / aspectRatio;

        if (imageHeight + frameOverheadPercent > maxHeight) {
          imageHeight = maxHeight - frameOverheadPercent;
          imageWidth = imageHeight * aspectRatio;
        }

        width = imageWidth;
        height = imageHeight + frameOverheadPercent;

        logger.debug('upload', 'Calculated dimensions', {
          originalWidth: img.width,
          originalHeight: img.height,
          aspectRatio,
          imageContentWidth: imageWidth,
          imageContentHeight: imageHeight,
          nodeWidth: width,
          nodeHeight: height,
          frameOverhead: frameOverheadPercent
        });
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('node[node_type]', file.type.startsWith('video/') ? 'video' : 'image');
      formData.append('node[position][x]', '50');
      formData.append('node[position][y]', '50');
      formData.append('node[size][width]', String(width));
      formData.append('node[size][height]', String(height));
      formData.append('node[z_index]', String(newZ));
      formData.append('node[frame_style]', 'window');

      logger.debug('api', `POST ${apiUrl(`/slides/${currentSlideId}/nodes`)}`);

      const response = await fetch(apiUrl(`/slides/${currentSlideId}/nodes`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to create image node');

      const data = await response.json();
      logger.info('api', 'Image node created', {
        nodeId: data.node.id,
        mediaUrl: data.node.media_url,
        sourceUrl: data.node.source_url,
        hasMedia: data.node.has_media,
        fullNode: data.node
      });

      const imageUrl = data.node.media_url || data.node.display_url || data.node.source_url || '';
      logger.debug('upload', `Using image URL: ${imageUrl}`);

      const newNode: Node = {
        id: `node-${data.node.id}`,
        type: data.node.node_type as 'image' | 'video',
        position: data.node.position || { x: 50, y: 50 },
        size: data.node.size || { width, height },
        zIndex: data.node.z_index || newZ,
        data: {
          content: imageUrl,
          title: data.node.title || '',
          frameStyle: data.node.frame_style || 'browser',
        } as ImageNodeData
      };
      setNodes(prevNodes => {
        const updated = [...prevNodes, newNode];
        if (currentSlideId) {
          setSlideNodesCache(prev => ({ ...prev, [currentSlideId]: updated }));
        }
        return updated;
      });
      loadRecentImages();
    } catch (error) {
      logger.error('upload', 'Failed to add image node', error);
    }
  };

  const addImageFromUrl = async (imageUrl: string) => {
    if (!currentSlideId) return;

    const maxZ = nodes.length > 0 ? Math.max(...nodes.map(n => n.zIndex)) : 0;
    const newZ = maxZ + 1;

    logger.info('upload', 'Adding image from URL', { imageUrl });

    try {
      const response = await fetch(apiUrl(`/slides/${currentSlideId}/nodes`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          node: {
            node_type: 'image',
            source_url: imageUrl,
            position: { x: 50, y: 50 },
            size: { width: 50, height: 50 },
            z_index: newZ,
            frame_style: 'window'
          }
        }),
      });

      if (!response.ok) throw new Error('Failed to create image node from URL');

      const data = await response.json();
      logger.info('api', 'Image node created from URL', { nodeId: data.node.id });

      const newNode: Node = {
        id: `node-${data.node.id}`,
        type: 'image',
        position: data.node.position || { x: 50, y: 50 },
        size: data.node.size || { width: 50, height: 50 },
        zIndex: data.node.z_index || newZ,
        data: {
          content: data.node.media_url || data.node.display_url || data.node.source_url || imageUrl,
          title: data.node.title || '',
          frameStyle: data.node.frame_style || 'browser',
        } as ImageNodeData
      };
      setNodes(prevNodes => {
        const updated = [...prevNodes, newNode];
        if (currentSlideId) {
          setSlideNodesCache(prev => ({ ...prev, [currentSlideId]: updated }));
        }
        return updated;
      });
    } catch (error) {
      logger.error('upload', 'Failed to add image node from URL', error);
    }
  };

  const addIframeNode = async (url: string) => {
    if (!currentSlideId) return;

    const maxZ = nodes.length > 0 ? Math.max(...nodes.map(n => n.zIndex)) : 0;
    const newZ = maxZ + 1;

    try {
      const response = await fetch(apiUrl(`/slides/${currentSlideId}/nodes`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          node: {
            node_type: 'iframe',
            source_url: url,
            frame_style: 'browser',
            position: { x: 50, y: 50 },
            size: { width: 95, height: 85 },
            z_index: newZ
          }
        }),
      });

      if (!response.ok) throw new Error('Failed to create iframe node');

      const data = await response.json();
      const newNode: Node = {
        id: `node-${data.node.id}`,
        type: 'iframe',
        position: data.node.position || { x: 50, y: 50 },
        size: data.node.size || { width: 95, height: 85 },
        zIndex: data.node.z_index || newZ,
        data: {
          content: data.node.source_url || url,
          title: data.node.title || '',
          frameStyle: data.node.frame_style || 'browser',
        } as IframeNodeData
      };
      setNodes(prevNodes => {
        const updated = [...prevNodes, newNode];
        if (currentSlideId) {
          setSlideNodesCache(prev => ({ ...prev, [currentSlideId]: updated }));
        }
        return updated;
      });

      setRecentUrls(prev => {
        const updated = [url, ...prev.filter(u => u !== url)].slice(0, 10);
        localStorage.setItem('recentUrls', JSON.stringify(updated));
        return updated;
      });

      setShowUrlPanel(false);
    } catch (error) {
      console.error('Failed to add iframe node:', error);
    }
  };

  return {
    loadRecentImages,
    updateNode,
    updateNodeData,
    deleteNode,
    duplicateNode,
    bringToFront,
    toggleFrameStyle,
    addTextNode,
    addImageNode,
    addImageFromUrl,
    addIframeNode,
  };
}
